import {
  chakra,
  IconButton,
  Image,
  Input,
  Presence,
  Stack,
  Text,
  Textarea,
  HStack,
  ButtonProps,
  Heading,
} from "@chakra-ui/react";
import {ChangeEvent, ComponentProps, memo, useCallback, useMemo} from "react";
import {Field} from "../../../../../../../../components/ui/field";
import {DiagramAnyObject, ClientDiagramNode, OBJECT_INFOS, ClientDiagramConnection} from "../../utility/types";
import {Button} from "../../../../../../../../components/ui/button";
import {PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger} from "../../../../../../../../components/ui/popover";
import {DocumentDuplicateIcon, TrashIcon} from "@heroicons/react/16/solid";
import {COLOR_PALETTES, ColorPalette} from "../../utility/colors";
import {getIcon, ICON_PACKS} from "../../icons";
import {useUiPointerEvents} from "../../utility/uiPointerEventsContext";

const POPOVER_POSITIONING: ComponentProps<typeof PopoverRoot>["positioning"] = {placement: "bottom-end"};

export const PropertiesPane = memo(
  ({
    selectedObject,
    onUpdateObject,
    onDuplicateObject,
    onDeleteObject,
  }: {
    selectedObject: DiagramAnyObject | null;
    onUpdateObject: (object: DiagramAnyObject) => void;
    onDuplicateObject: (object: DiagramAnyObject) => void;
    onDeleteObject: (object: DiagramAnyObject) => void;
  }) => {
    return (
      <Presence
        present={selectedObject != null}
        animationName={{_open: "slide-from-right, fade-in"}}
        animationDuration="moderate"
        lazyMount
        unmountOnExit
        flex="1 1 0px"
      >
        {selectedObject != null && (
          <PropertiesPaneInner
            selectedObject={selectedObject}
            onUpdateObject={onUpdateObject}
            onDuplicateObject={onDuplicateObject}
            onDeleteObject={onDeleteObject}
          />
        )}
      </Presence>
    );
  },
);

const PropertiesPaneInner = memo(
  ({
    selectedObject,
    onUpdateObject,
    onDuplicateObject,
    onDeleteObject,
  }: {
    selectedObject: DiagramAnyObject;
    onUpdateObject: (object: DiagramAnyObject) => void;
    onDuplicateObject: (object: DiagramAnyObject) => void;
    onDeleteObject: (object: DiagramAnyObject) => void;
  }) => {
    const pointerEvents = useUiPointerEvents();

    const onClickDuplicate = useCallback(() => {
      onDuplicateObject(selectedObject);
    }, [onDuplicateObject, selectedObject]);

    const onClickDelete = useCallback(() => {
      onDeleteObject(selectedObject);
    }, [onDeleteObject, selectedObject]);

    const onUpdateNode = useCallback(
      (node: ClientDiagramNode) => {
        onUpdateObject({type: "Node", node});
      },
      [onUpdateObject],
    );

    const onUpdateConnection = useCallback(
      (connection: ClientDiagramConnection) => {
        onUpdateObject({type: "Connection", connection});
      },
      [onUpdateObject],
    );

    return (
      <Stack
        pointerEvents={pointerEvents}
        height="100%"
        bg="white"
        rounded="md"
        overflow="hidden"
        shadow="md"
        p={4}
        gap={4}
        width="256px"
      >
        <Text fontWeight="medium" mb={2}>
          Selected {OBJECT_INFOS[selectedObject.type].label}
        </Text>
        {selectedObject.type === "Node" && <NodeProperties node={selectedObject.node} onUpdateNode={onUpdateNode} />}
        {selectedObject.type === "Connection" && (
          <ConnectionProperties connection={selectedObject.connection} onUpdateConnection={onUpdateConnection} />
        )}
        {selectedObject.type === "ConnectionAnchor" && (
          <ConnectionProperties connection={selectedObject.connection} onUpdateConnection={onUpdateConnection} />
        )}
        <chakra.div flex="1 1 0px"></chakra.div>
        {selectedObject.type === "Node" && (
          <Button colorPalette="blue" variant="subtle" onClick={onClickDuplicate}>
            <DocumentDuplicateIcon />
            Duplicate node
          </Button>
        )}
        <Button colorPalette="red" variant="subtle" onClick={onClickDelete}>
          <TrashIcon />
          Delete {OBJECT_INFOS[selectedObject.type].label}
        </Button>
      </Stack>
    );
  },
);

const NodeProperties = memo(
  ({node, onUpdateNode}: {node: ClientDiagramNode; onUpdateNode: (node: ClientDiagramNode) => void}) => {
    const onLabelChange = useCallback(
      (ev: ChangeEvent<HTMLInputElement>) => {
        onUpdateNode({...node, label: ev.target.value});
      },
      [node, onUpdateNode],
    );

    const onDescriptionChange = useCallback(
      (ev: ChangeEvent<HTMLTextAreaElement>) => {
        onUpdateNode({...node, description: ev.target.value});
      },
      [node, onUpdateNode],
    );

    const onIconChange = useCallback(
      (icon: string) => {
        onUpdateNode({...node, icon});
      },
      [node, onUpdateNode],
    );

    const icons = useMemo(
      () => ICON_PACKS.flatMap(pack => pack.icons).toSorted((a, b) => a.label.localeCompare(b.label)),
      [],
    );

    const icon = useMemo(() => getIcon(node.icon), [node.icon]);

    return (
      <>
        <Field label="Label">
          <Input value={node.label} onChange={onLabelChange} />
        </Field>
        <Field label="Description">
          <Textarea value={node.description} onChange={onDescriptionChange} autoresize />
        </Field>
        <Field
          label={
            <>
              Icon:{" "}
              <Text as="span" fontWeight="normal" color="fg.muted">
                {icon.label}
              </Text>{" "}
            </>
          }
        >
          <PopoverRoot positioning={POPOVER_POSITIONING} lazyMount unmountOnExit>
            <PopoverTrigger asChild>
              <Button size="2xl" py={0} px={1} alignSelf="stretch" gap={4} variant="outline">
                <Image boxSize={12} src={icon.url} />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverBody display="flex" flexFlow="column" gap={4} overflow="hidden">
                <Heading size="md">Select an icon:</Heading>
                <HStack
                  flexWrap="wrap"
                  maxHeight="360px"
                  overflow="hidden"
                  overflowY="scroll"
                  alignItems="stretch"
                  gap={2}
                  borderWidth="1px"
                  rounded="md"
                  p={4}
                >
                  {icons.map(icon => (
                    <Stack key={icon.id} gap={2} width="64px">
                      <IconButton variant="ghost" size="2xl" overflow="hidden" onClick={() => onIconChange(icon.id)}>
                        <Image src={icon.url} />
                      </IconButton>
                      <Text
                        flex="0 0 auto"
                        fontSize="2xs"
                        alignSelf="center"
                        fontWeight="medium"
                        color="fg.muted"
                        lineHeight={1}
                        textAlign="center"
                      >
                        {icon.label}
                      </Text>
                    </Stack>
                  ))}
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </PopoverRoot>
        </Field>
      </>
    );
  },
);

const ColorPalettePicker = memo(
  ({palette, onPaletteChange}: {palette: ColorPalette; onPaletteChange: (palette: ColorPalette) => void}) => {
    const selectedProps = useMemo<ButtonProps>(
      () => ({outlineWidth: "2px", outlineColor: "black", outlineStyle: "solid", outlineOffset: "2px"}),
      [],
    );

    return (
      <HStack flexWrap="wrap">
        {COLOR_PALETTES.map(p => (
          <Button
            key={p}
            colorPalette={p}
            size="2xs"
            onClick={() => onPaletteChange(p)}
            {...(p === palette ? selectedProps : {})}
          />
        ))}
      </HStack>
    );
  },
);

const ConnectionProperties = memo(
  ({
    connection,
    onUpdateConnection,
  }: {
    connection: ClientDiagramConnection;
    onUpdateConnection: (connection: ClientDiagramConnection) => void;
  }) => {
    const onLabelChange = useCallback(
      (ev: ChangeEvent<HTMLInputElement>) => {
        onUpdateConnection({...connection, label: ev.target.value});
      },
      [connection, onUpdateConnection],
    );

    const onPaletteChange = useCallback(
      (palette: ColorPalette) => {
        onUpdateConnection({...connection, color: palette});
      },
      [connection, onUpdateConnection],
    );

    return (
      <>
        <Field label="Label">
          <Input value={connection.label} onChange={onLabelChange} />
        </Field>
        <Field label="Color">
          <ColorPalettePicker palette={connection.color} onPaletteChange={onPaletteChange} />
        </Field>
      </>
    );
  },
);
